import { AlertCircle, CheckCircle2, Download, FileSpreadsheet, Upload } from "lucide-react";
import { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { Button } from "../../../components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/dialog";
import { Label } from "../../../components/label";
import { SelectV2 } from "../../../components/select-v2";
import { useAuthSession } from "../../auth/shared";
import { useCampaignImportOrganizations, useImportCampaigns } from "../hooks";
import { CampaignImportResponse } from "../types";

interface CampaignImportDialogProps {
  open: boolean;
  currentOrganizationId?: string;
  onOpenChange: (open: boolean) => void;
  onImported: (result: CampaignImportResponse) => Promise<void> | void;
}

function isSpreadsheet(file: File) {
  return file.name.toLowerCase().endsWith(".xlsx");
}

export function CampaignImportDialog({
  open,
  currentOrganizationId,
  onOpenChange,
  onImported,
}: CampaignImportDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuthSession();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [organizationId, setOrganizationId] = useState(currentOrganizationId ?? "");
  const [fileError, setFileError] = useState<string | null>(null);
  const [success, setSuccess] = useState<CampaignImportResponse | null>(null);
  const { error, execute, importErrors, isImporting, reset } = useImportCampaigns();
  const canChooseOrganization = user?.role === "super_admin";
  const { organizations } = useCampaignImportOrganizations(canChooseOrganization);

  const organizationOptions = [
    ...(currentOrganizationId &&
    !organizations.some((organization) => organization.id === currentOrganizationId)
      ? [
          {
            value: currentOrganizationId,
            label: t("campaign.import.organization_current", { id: currentOrganizationId }),
            textValue: currentOrganizationId,
          },
        ]
      : []),
    ...organizations.map((organization) => ({
      value: organization.id,
      label: organization.state
        ? `${organization.name} (${organization.state})`
        : organization.name,
      textValue: `${organization.name} ${organization.state ?? ""}`,
    })),
  ];

  const close = () => {
    if (isImporting) return;

    setFile(null);
    setFileError(null);
    setSuccess(null);
    reset();
    onOpenChange(false);
  };

  const downloadTemplate = () => {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet([
        ["ExternalCampaignKey", "Name", "Description", "Status", "StartDate", "EndDate"],
        [
          "community-2027",
          "Community 2027",
          "Sample campaign hierarchy",
          "Draft",
          "2027-01-01",
          "2027-12-31",
        ],
      ]),
      "Campaigns",
    );
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet([
        ["ExternalCampaignKey", "ExternalProgramKey", "Name", "Description", "Status"],
        ["community-2027", "community-events", "Community events", "Sample event program", "Draft"],
      ]),
      "Programs",
    );
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet([
        ["ExternalProgramKey", "ActivityName", "Type", "EventId", "SurveyId"],
        ["community-events", "Community kickoff", "Event", "", ""],
      ]),
      "Opportunities",
    );
    XLSX.writeFile(workbook, "campaign-import-template.xlsx");
  };

  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    setSuccess(null);
    reset();
    if (nextFile && !isSpreadsheet(nextFile)) {
      setFile(null);
      setFileError(t("campaign.import.invalid_file"));
      return;
    }

    setFileError(null);
    setFile(nextFile);
  };

  const submit = async () => {
    if (!file || isImporting) return;

    try {
      const result = await execute(file, organizationId);
      setSuccess(result);
      await onImported(result);
    } catch {
      return;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : close())}
    >
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-[#DDEBE3] bg-white p-6">
        <DialogHeader>
          <DialogTitle className="text-xl text-[#0F172A]">{t("campaign.import.title")}</DialogTitle>
          <DialogDescription className="text-[#52606D]">
            {t("campaign.import.description")}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="space-y-5 py-2">
            <div className="flex items-start gap-3 rounded-xl border border-[#B7E4C7] bg-[#F3FBF6] p-4">
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-[#137333]" />
              <div>
                <p className="font-semibold text-[#0F5132]">{t("campaign.import.success_title")}</p>
                <p className="mt-1 text-sm text-[#356859]">
                  {t("campaign.import.success_description")}
                </p>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                ["campaignsCreated", success.campaignsCreated],
                ["programsCreated", success.programsCreated],
                ["opportunitiesCreated", success.opportunitiesCreated],
              ].map(([key, value]) => (
                <div
                  key={key}
                  className="rounded-xl border border-[#DDEBE3] bg-[#F8FCF9] p-4"
                >
                  <p className="text-2xl font-semibold text-[#004232]">{value}</p>
                  <p className="mt-1 text-sm text-[#52606D]">
                    {t(`campaign.import.summary.${key}`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-5 py-2">
            <Button
              variant="brand-outline"
              onClick={downloadTemplate}
              disabled={isImporting}
            >
              <Download className="h-4 w-4" />
              {t("campaign.import.download_template")}
            </Button>
            <div className="rounded-xl border border-[#DDEBE3] bg-[#F8FCF9] p-4 text-sm text-[#40513F]">
              <p className="font-semibold text-[#0F172A]">{t("campaign.import.workbook_title")}</p>
              <p className="mt-1">{t("campaign.import.workbook_description")}</p>
            </div>

            <div>
              {canChooseOrganization ? (
                <SelectV2
                  value={organizationId}
                  onChange={setOrganizationId}
                  options={organizationOptions}
                  label={t("campaign.import.organization_label")}
                  ariaLabel={t("campaign.import.organization_label")}
                  placeholder={t("campaign.import.organization_placeholder")}
                  disabled={isImporting}
                  triggerClassName="mt-1 border-[#D5DDE7]"
                  contentClassName="max-h-64"
                />
              ) : (
                <div>
                  <Label>{t("campaign.import.organization_label")}</Label>
                  <div className="mt-2 flex min-h-10 items-center rounded-md border border-[#D5DDE7] bg-[#F3F3F5] px-3 text-sm text-[#52606D]">
                    {currentOrganizationId ?? t("campaign.import.organization_current_missing")}
                  </div>
                </div>
              )}
              <p className="mt-1 text-xs text-[#52606D]">
                {t("campaign.import.organization_hint")}
              </p>
            </div>

            <div>
              <Label>{t("campaign.import.file_label")}</Label>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="sr-only"
                onChange={selectFile}
                disabled={isImporting}
              />
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={isImporting}
                className="mt-2 flex w-full items-center gap-3 rounded-xl border border-dashed border-[#8AB89B] bg-[#F8FCF9] p-4 text-left transition hover:border-[#004232] hover:bg-[#F3FBF6] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FileSpreadsheet className="h-8 w-8 text-[#0B6B45]" />
                <span className="min-w-0">
                  <span className="block truncate font-medium text-[#0F172A]">
                    {file?.name ?? t("campaign.import.choose_file")}
                  </span>
                  <span className="mt-1 block text-sm text-[#52606D]">
                    {file
                      ? t("campaign.import.file_size", { size: Math.ceil(file.size / 1024) })
                      : t("campaign.import.file_hint")}
                  </span>
                </span>
              </button>
            </div>

            {fileError || error ? (
              <div className="rounded-xl border border-[#F3B6B6] bg-[#FFF6F6] p-4">
                <div className="flex gap-3 text-[#9B1C1C]">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                  <div>
                    <p className="font-semibold">{t("campaign.import.error_title")}</p>
                    <p className="mt-1 text-sm">{fileError ?? error}</p>
                  </div>
                </div>
                {importErrors.length > 0 ? (
                  <div className="mt-4 overflow-x-auto rounded-lg border border-[#F6D0D0] bg-white">
                    <table className="min-w-full text-left text-sm">
                      <thead className="bg-[#FFF0F0] text-xs font-semibold uppercase tracking-wide text-[#7F1D1D]">
                        <tr>
                          <th className="px-3 py-2">{t("campaign.import.errors.sheet")}</th>
                          <th className="px-3 py-2">{t("campaign.import.errors.row")}</th>
                          <th className="px-3 py-2">{t("campaign.import.errors.field")}</th>
                          <th className="px-3 py-2">{t("campaign.import.errors.message")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F6D0D0] text-[#4A1D1D]">
                        {importErrors.map((item, index) => (
                          <tr key={`${item.sheet}-${item.row}-${item.field}-${index}`}>
                            <td className="px-3 py-2">{item.sheet}</td>
                            <td className="px-3 py-2">{item.row ?? "-"}</td>
                            <td className="px-3 py-2">{item.field ?? "-"}</td>
                            <td className="px-3 py-2">{item.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="brand-outline"
            onClick={close}
            disabled={isImporting}
          >
            {success ? t("campaign.import.done") : t("common.cancel")}
          </Button>
          {!success ? (
            <Button
              variant="brand-secondary"
              onClick={() => void submit()}
              disabled={!file || isImporting}
            >
              <Upload className="h-4 w-4" />
              {isImporting ? t("campaign.import.importing") : t("campaign.import.submit")}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
